import { debounce } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useListingNotificationContext } from 'src/contexts/ListingNotificationContext';
import { entityTypeNumberToEntityTypeEnum } from 'src/utils/activityLogUtils';
import { ActionOutboxEntityType, Listing } from 'src/WebApiController';

export const useRefreshListingOnNotification = () => {
  const { hubConnection } = useListingNotificationContext();

  const { refreshEventForItems } = useCatalogDataContext();
  const { setActivePosEntity, posEntityId, posEntity } =
    useActivePosEntityContext<Listing>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetActivePosEntity = useCallback(
    debounce((posEntityId: number) => {
      setActivePosEntity(posEntityId, undefined, true);
    }, 3000),
    []
  );

  const [_, setItemsIdsToRefresh] = useState<number[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetItemsIdsToRefresh = useCallback(
    debounce((ids: number[]) => {
      refreshEventForItems(ids, ActionOutboxEntityType.Listing);
      setItemsIdsToRefresh([]);
    }, 3_000),
    [refreshEventForItems]
  );

  useEffect(() => {
    const messageHandler = async (message: string) => {
      try {
        const messageList = JSON.parse(message);
        for (const m of messageList) {
          console.debug(
            'useRefreshListingOnNotification - got new notification',
            m,
            hubConnection
          );

          // Refresh relevant listing
          if (m.EntityId === posEntityId && posEntityId) {
            debouncedSetActivePosEntity(posEntityId);
          }
          const entityType = entityTypeNumberToEntityTypeEnum(m.EntityType);
          if (entityType === ActionOutboxEntityType.Listing && m.EntityId) {
            setItemsIdsToRefresh((prev) => {
              const newIds: number[] = Array.from(
                new Set([...prev, m.EntityId])
              );
              debouncedSetItemsIdsToRefresh(newIds);
              return newIds;
            });
          }
        }
      } catch (e) {
        console.error(
          'useRefreshListingOnNotification - malformed SignalR Message: ' +
            message
        );
      }
    };

    if (hubConnection) {
      hubConnection.on('new', messageHandler);
    }

    return () => {
      if (hubConnection) {
        hubConnection.off('new', messageHandler);
      }
    };
  }, [
    debouncedSetActivePosEntity,
    debouncedSetItemsIdsToRefresh,
    hubConnection,
    posEntity?.rowVer,
    posEntityId,
  ]);
};
